import * as React from 'react'
import debounce from 'lodash.debounce'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import {
  TypeOption,
  findAcceptedTypeOptionForBlock,
  getAcceptedTypeOptions,
  getAllPossibleTypeOptions,
} from '@basehub/data/replicache/schema/block/reference/util'
import {
  Block,
  ReferenceBlock,
  isRootBlock,
} from '@basehub/data/replicache/index'
import { TreeNode } from '@basehub/utils/tree/index'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useVirtualizer } from '@tanstack/react-virtual'
import { InstanceSearchParamsMeta } from '@basehub/data/replicache/syncers'
import { throttle } from 'lodash'
import { useTreeSlice } from '~/hooks/use-tree-slice'

export type RowsMeta = Map<
  number,
  { shouldRenderTypeOption: boolean; typeOption: TypeOption | null }
>

export const usePossibleReferencesVirtualized = (opts: {
  block: ReferenceBlock
  disabled?: boolean
}) => {
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [meta, setMeta] = React.useState<InstanceSearchParamsMeta | undefined>()
  const scrollableElementRef = React.useRef<HTMLDivElement>(null)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const getPath = useWorkingTreeStore((s) => s.tree?.getPath)
  const rootBlockId = useWorkingTreeStore((s) => s.tree?.root.value.id) ?? ''
  const tree = useWorkingTreeStore((s) => s.tree)
  const referenceableSet = useTreeSlice(
    tree,
    (s) => {
      const set = s.compoundIndexes.get('referenceable')
      return Array.from(set ?? []).sort()
    },
    'deep'
  )
  const componentsSet = useTreeSlice(
    tree,
    (s) => {
      const set = s.compoundIndexes.get('components')
      return Array.from(set ?? [])
    },
    'deep'
  )
  const getChildrenKeys = useWorkingTreeStore((s) => s.tree?.getChildrenKeys)
  const { data } = useRepoReplicache()
  const replicache = data?.replicache

  const typeOptions = React.useMemo(() => {
    if (!getNode || !componentsSet || !getChildrenKeys) return []
    return getAllPossibleTypeOptions(
      { getNode, componentsSet: new Set(componentsSet), getChildrenKeys },
      opts.block
    )
  }, [componentsSet, getChildrenKeys, getNode, opts.block])

  const acceptedTypeOptions = React.useMemo(() => {
    if (!getNode) return []
    return getAcceptedTypeOptions({
      refBlock: opts.block,
      blockTreeManager: { getNode },
    })
  }, [getNode, opts.block])

  const rowsMeta = React.useRef<RowsMeta>(new Map())
  const componentSourceBlockIds = React.useMemo(
    () => opts.block.constraints.acceptedTypes ?? [],
    [opts.block.constraints.acceptedTypes]
  )

  // Debounce the search to reduce expensive operations
  const debouncedSetSearch = React.useMemo(
    () => debounce((value: string) => setDebouncedSearch(value), 300),
    []
  )

  React.useEffect(() => {
    debouncedSetSearch(search)
    return () => debouncedSetSearch.cancel()
  }, [search, debouncedSetSearch])

  const count = React.useMemo(() => {
    return meta?.total ?? 0
  }, [meta])

  const virtualizer = useVirtualizer({
    count,
    overscan: 0,
    getScrollElement: () => scrollableElementRef.current,
    estimateSize: (index) => {
      const meta = rowsMeta.current.get(index)
      if (!meta) return 32
      if (meta.shouldRenderTypeOption && meta.typeOption) {
        if (index === 0) return 56
        return 64
      }

      return 32
    },
  })

  const { data: instanceSearchResult } = useCustomSyncerSub(
    opts.disabled || !componentSourceBlockIds.length ? undefined : replicache,
    {
      syncer: 'instanceSearch',
      params: {
        q: debouncedSearch,
        window: {
          from: 0,
          to: virtualizer.range?.endIndex || 8,
        },
        rootBlockId,
        componentSourceBlockIds,
        disableLinkExisting: !!opts.block.constraints.disableLinkExisting,
        disableCreateNew: !!opts.block.constraints.disableCreateNew,
      },
      extraDeps: referenceableSet,
    }
  )

  React.useEffect(() => {
    if (!instanceSearchResult) return
    setMeta(instanceSearchResult)
  }, [instanceSearchResult])

  const throttledScrollToTop = React.useMemo(
    () =>
      throttle(() => {
        try {
          virtualizer.scrollToIndex(0)
        } catch (error) {
          // fail silently if scrollToIndex fails
        }
      }, 300),
    [virtualizer]
  )

  React.useEffect(() => {
    throttledScrollToTop()
  }, [search, throttledScrollToTop])

  const possibleReferenceNodes = React.useMemo(() => {
    if (!getNode || !getPath || !referenceableSet) return []

    const filteredNodes: Array<TreeNode<Block>> = []

    // Single pass through referenceable nodes
    for (const id of referenceableSet) {
      const node = getNode(id)
      if (!node) continue
      const nodeBlock = node.value

      // Early exit conditions
      if (isRootBlock(nodeBlock) || nodeBlock.id === opts.block.id) continue

      // Type option check
      if (
        findAcceptedTypeOptionForBlock({
          refBlock: opts.block,
          evaluatedBlock: nodeBlock,
          typeOptions,
        })
      ) {
        filteredNodes.push(node)
      }
    }

    return filteredNodes
  }, [getNode, getPath, referenceableSet, typeOptions, opts.block])

  return {
    acceptedTypeOptions,
    typeOptions,
    possibleReferenceNodes,
    scrollableElementRef,
    setSearch,
    search,
    virtualizer,
    rowsMeta,
    instanceSearchResult,
    componentSourceBlockIds,
    instancesPerComponent: meta?.totalPerComponent,
  }
}
